const PARALLAX_SELECTOR = "[data-parallax]",
  DISABLED_CLASS = "is-disabled",
  MAIN_CONTENT_CLASS = "main-content",
  SECTION_CLASS = "shopify-section",
  PARALLAX_ATTR = "data-parallax",
  INTENSITY_ATTR = "data-parallax-intensity",
  STATIC_ATTR = "data-parallax-static",
  SINGLE_ATTR = "data-parallax-single",
  DISABLE_MOBILE_ATTR = "data-parallax-disable-on-mobile";

function getOrientation() {
  return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
}

class ParallaxHandler {
  constructor(container) {
    this.container = container;
    this.percentage = 0;
    this.percentageFull = 0;
    this.animation = container.getAttribute("data-parallax");
    this.disableOnMobile = container.hasAttribute(
      "data-parallax-disable-on-mobile"
    );
    this.viewportHeight = Math.max(
      document.documentElement.clientHeight,
      window.innerHeight || 0
    );
    this.orientation = this.getOrientation();
    this.scrollEvent = () => this.updateParallax();
    this.resizeEvent = () => this.updateHeight();

    requestAnimationFrame(() => {
      this.containerHeight = container.offsetHeight;
      this.footerOffset = 0.15 * this.containerHeight;
      this.cachedPosition = this.footerOffset;
      this.cachedPercentage = 0;
      this.init();
    });
  }

  init() {
    this.updateParallax();
    document.addEventListener("theme:scroll", this.scrollEvent);
    document.addEventListener("theme:resize", this.resizeEvent);
  }

  unload() {
    document.removeEventListener("theme:scroll", this.scrollEvent);
    document.removeEventListener("theme:resize", this.resizeEvent);
  }

  updateParallax() {
    if (this.container.classList.contains("is-disabled")) return;
    if (this.disableOnMobile && this.isMobile()) return;

    const scrollTop = Math.round(window.scrollY);
    const scrollBottom = scrollTop + this.viewportHeight;

    let containerTop = Math.round(
      this.container.getBoundingClientRect().top + scrollTop
    );


    if (this.animation === "footer") {
      let sibling = document.getElementById("MainContent");
      if (sibling) {
        containerTop = Math.round(
          sibling.getBoundingClientRect().bottom + scrollTop
        );
      }
    }
    const containerHeight = this.container.offsetHeight;
    if (
      containerTop < scrollBottom &&
      containerTop + containerHeight >= scrollTop
    ) {
      const visibleBottom = scrollBottom - containerTop;
      this.percentage =
        (100 * (visibleBottom - containerHeight / 2)) / this.viewportHeight;
      this.percentageFull = Number(visibleBottom / containerHeight).toFixed(2);
      this.percentageVisible = Number(
        (100 * visibleBottom) / (this.viewportHeight + containerHeight)
      ).toFixed(2);
      switch (this.animation) {
        case "circle":
          this.animateCircleText();
          break;
        case "horizontal":
        case "vertical":
        case "diagonal":
          this.animateOverlappingImages();
          break;
        case "offset-ltr":
        case "offset-rtl":
          this.applyOffsetX();
          break;
        case "zoom-on-scroll":
          this.zoomInOut();
          break;
        case "footer":
          this.animateFooter();
          break;
      }
    }
  }

 applyOffsetX() {
  const isStatic = this.container.hasAttribute("data-parallax-static");
  const intensity = this.container.getAttribute("data-parallax-intensity") || 100;
  let baseOffset = this.animation === "offset-ltr" ? intensity : 0;

  if (isStatic) baseOffset = intensity / 2;
  const direction = this.animation === "offset-ltr" ? 1 : -1;
  const offset = (intensity * this.percentageVisible) / 100 - baseOffset;
  
  let finalOffset = Number(offset * direction).toFixed(2);
  finalOffset = Math.max(0, Math.min(1, finalOffset)); 
  const clamp = (n,min,max) => Math.max(min, Math.min(max, n));

  const progress = clamp((this.percentageVisible - 40) / 60, 0, 1);
  const value = 1 - progress;   

  const scaleProgress = clamp(this.percentageVisible, 0, 100);
  const scaleValue = 1.5 - (scaleProgress / 100);

  this.container.style.setProperty("--offsetX", `${value * 1.2}`);           
  this.container.style.setProperty("--scale", `${scaleValue * 2}`);
}

  animateCircleText() {
    const rotateAmount = ((70 * this.percentage) / 100) * 1;
    if (this.percentage > 0) {
      this.container.style.setProperty("--rotate", `${35 + rotateAmount}deg`);
    }
  }

  animateOverlappingImages() {
    const intensity =
      0.25 * this.container.getAttribute("data-parallax-intensity");
    const halfIntensity = intensity / 2;
    const isSingle = this.container.hasAttribute("data-parallax-single");
    const value = (intensity * this.percentage) / 100;

    let xPrimary = 0,
      yPrimary = 0,
      xSecondary = 0,
      ySecondary = 0;

    if (["horizontal", "diagonal"].includes(this.animation)) {
      xPrimary = -halfIntensity + value;
      xSecondary = halfIntensity - value;
    }

    if (["vertical", "diagonal"].includes(this.animation)) {
      yPrimary = halfIntensity - value;
      ySecondary = -halfIntensity + value;
    }

    this.container.style.setProperty("--transformX-primary", `${xPrimary}%`);
    this.container.style.setProperty("--transformY-primary", `${yPrimary}%`);

    if (!isSingle) {
      this.container.style.setProperty(
        "--transformX-secondary",
        `${xSecondary}%`
      );
      this.container.style.setProperty(
        "--transformY-secondary",
        `${ySecondary}%`
      );
    }
  }

  animateFooter() {
    const offset = this.footerOffset * this.percentageFull;
    const percentage = Number((offset / this.footerOffset) * 100).toFixed(2);
    let newY = Number(this.footerOffset - offset).toFixed(2);

    if (this.percentageFull >= 1) newY = 0;

    if (this.cachedPercentage !== percentage) {
      this.container.style.setProperty("--transformY", `${newY}`);
      this.cachedPercentage = percentage;
      this.cachedPosition = newY;
    }
  }

  zoomInOut() {
    const intensity = 0.2; // Equivalent to 20% zoom scale variation
    const scale = 1 + (intensity * this.percentageVisible) / 100;
    const clampedScale = Math.max(scale, 1); // Prevent scaling below 1
    this.container.style.setProperty("--scale", clampedScale.toFixed(4));
  }

  updateHeight() {
    const isDesktop = matchMedia("(min-width: 1024px)").matches;
    if (this.orientation !== this.getOrientation() || isDesktop) {
      this.viewportHeight = Math.round(
        Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
      );
      this.containerHeight = this.container.offsetHeight;
      this.orientation = this.getOrientation();
    }
  }

  getOrientation() {
    return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
  }

  isMobile() {
    return /Mobi|Android/i.test(navigator.userAgent);
  }
}

// Optional: apply to all elements on DOM ready
// document.querySelectorAll(PARALLAX_SELECTOR).forEach(el => new ParallaxEffect(el));
